import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle, ArrowLeft, Download, Copy, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { orderService, type OrderResponse } from "@/services";
import { cn } from "@/lib/utils";

/**
 * Página de confirmación de pedido
 *
 * Muestra la confirmación exitosa de una compra con el código QR
 * para canjear el pedido en el establecimiento.
 * Optimizada para dispositivos móviles con layout responsive.
 */
export function OrderConfirmationPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadOrder = async () => {
      if (!orderId) {
        setError("ID de pedido no válido");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const orderData = await orderService.getOrderById(orderId);

        if (!orderData) {
          setError("Pedido no encontrado");
        } else {
          setOrder(orderData);
        }
      } catch (err) {
        setError("Error al cargar el pedido");
        console.error("Error loading order:", err);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderId]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-CL", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyOrderId = async () => {
    if (order?.orderId) {
      try {
        await navigator.clipboard.writeText(order.orderId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Error copying to clipboard:", err);
      }
    }
  };

  const downloadQR = () => {
    const canvas = document.querySelector("canvas");
    if (canvas) {
      const link = document.createElement("a");
      link.download = `qr-${order?.orderId}.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6">
          <div className="max-w-2xl mx-auto space-y-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Skeleton className="h-48 w-48 mx-auto rounded-lg" />
                  <Skeleton className="h-6 w-32 mx-auto" />
                  <Skeleton className="h-4 w-48 mx-auto" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6">
          <div className="max-w-2xl mx-auto text-center">
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
                    <XCircle className="h-8 w-8 text-destructive" />
                  </div>
                  <h2 className="text-xl font-semibold text-destructive">
                    {error || "Pedido no encontrado"}
                  </h2>
                  <p className="text-muted-foreground">
                    No pudimos encontrar el pedido solicitado.
                  </p>
                  <Button onClick={() => navigate("/")} className="h-10">
                    Volver a la Tienda
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="flex items-center gap-2 h-10"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Volver a la Tienda</span>
              <span className="sm:hidden">Volver</span>
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                Confirmación de Pedido
              </h1>
              <p className="text-sm text-muted-foreground">
                Tu compra ha sido procesada exitosamente
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Mensaje de éxito */}
          <Card className="border-primary/20 bg-primary/5 hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-primary mb-2">
                    ¡Tu compra ha sido exitosa!
                  </h2>
                  <p className="text-muted-foreground">
                    Tu pedido ha sido procesado y está listo para ser recogido
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* QR Code */}
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="text-center text-foreground">
                Código QR de Canje
              </CardTitle>
              <p className="text-sm text-muted-foreground text-center">
                Muestra este código QR en el establecimiento para recoger tu
                pedido
              </p>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* QR Code */}
                <div className="flex justify-center">
                  <div className="p-4 bg-background rounded-lg shadow-sm border border-border">
                    <QRCodeSVG
                      value={order.qrCodePayload}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </div>

                {/* Botones de acción */}
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadQR}
                    className="flex items-center gap-2 h-10"
                  >
                    <Download className="h-4 w-4" />
                    Descargar QR
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyOrderId}
                    className={cn(
                      "flex items-center gap-2 h-10",
                      copied && "bg-primary/10 text-primary border-primary/20"
                    )}
                  >
                    <Copy className="h-4 w-4" />
                    {copied ? "¡Copiado!" : "Copiar ID"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Información del pedido */}
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="text-foreground">
                Detalles del Pedido
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Información básica */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Número de Pedido:
                  </span>
                  <p className="font-mono text-sm text-foreground">
                    {order.orderId}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Fecha:
                  </span>
                  <p className="text-sm text-foreground">
                    {formatDate(order.orderDate)}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Estado:
                  </span>
                  <Badge variant="default" className="mt-1">
                    {order.status === "paid" ? "Pagado" : order.status}
                  </Badge>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">
                    Total:
                  </span>
                  <p className="text-lg font-semibold text-primary">
                    {formatPrice(order.total)}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Lista de productos */}
              <div>
                <h4 className="font-medium mb-3 text-foreground">Productos:</h4>
                <div className="space-y-2">
                  {order.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex justify-between items-center text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">
                          {item.name}
                        </span>
                        <span className="text-muted-foreground">
                          x{item.quantity}
                        </span>
                      </div>
                      <span className="text-foreground">
                        {formatPrice(item.price * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Información del cliente */}
              <div>
                <h4 className="font-medium mb-2 text-foreground">Cliente:</h4>
                <p className="text-sm text-muted-foreground">
                  {order.user.nombres} {order.user.apellidos}
                </p>
                <p className="text-sm text-muted-foreground">
                  {order.user.email}
                </p>
                <div className="mt-2">
                  <Badge variant={order.user.id ? "secondary" : "outline"}>
                    {order.user.id
                      ? "Usuario Registrado"
                      : "Compra como Invitado"}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Botones de acción */}
          <div className="text-center space-y-3">
            <Button
              onClick={() => navigate("/")}
              size="lg"
              className="px-8 h-12"
            >
              Continuar Comprando
            </Button>

            {/* Botón "Ver mis Pedidos" - solo mostrar si el usuario está autenticado */}
            {order.user.id && (
              <div>
                <Button
                  onClick={() => navigate("/profile/payments")}
                  variant="outline"
                  size="lg"
                  className="px-8 h-12"
                >
                  Ver mis Pedidos
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Accede a tu historial completo de compras
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
